import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { hasAnalyticsConsent, hasMarketingConsent } from './CookieConsent';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    fbq: ((...args: unknown[]) => void) & {
      callMethod?: (...args: unknown[]) => void;
      queue: unknown[];
      loaded: boolean;
      version: string;
    };
    _fbq?: typeof window.fbq;
  }
}

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID;

let gaInitialized = false;
let fbInitialized = false;

/**
 * Inicializa Google Analytics (only if consent given)
 */
function initGA() {
  if (gaInitialized) return;
  if (!GA_MEASUREMENT_ID || GA_MEASUREMENT_ID === 'G-XXXXXXXXXX') return;
  if (!hasAnalyticsConsent()) {
    return;
  }

  // Add gtag script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize gtag (must use 'arguments' object, not spread args)
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  window.gtag = function() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    anonymize_ip: true, // GDPR compliance
  });

  gaInitialized = true;
}

/**
 * Inicializa Meta Pixel (only if consent given)
 */
function initMetaPixel() {
  if (fbInitialized) return;
  if (!META_PIXEL_ID || META_PIXEL_ID === 'XXXXXXXXXXXXXXX') return;
  if (!hasMarketingConsent()) {
    return;
  }

  // Meta Pixel base code - simplified version
  const n = function(...args: unknown[]) {
    if (n.callMethod) {
      n.callMethod(...args);
    } else {
      n.queue.push(args);
    }
  } as typeof window.fbq;

  n.queue = [];
  n.loaded = true;
  n.version = '2.0';

  if (!window._fbq) window._fbq = n;
  window.fbq = n;

  // Load the script
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);

  window.fbq('init', META_PIXEL_ID);
  window.fbq('track', 'PageView');

  fbInitialized = true;
}

/**
 * Componente de Analytics
 * Inicializa GA e Meta Pixel e rastreia mudanças de página
 * Only loads if user has given consent
 */
export function Analytics() {
  const location = useLocation();

  // Inicializa analytics uma vez (if consent given)
  useEffect(() => {
    // Não inicializa em localhost
    if (window.location.hostname === 'localhost') {
      console.log('[Analytics] Skipping initialization on localhost');
      return;
    }

    initGA();
    initMetaPixel();
  }, []);

  // Rastreia mudanças de página
  useEffect(() => {
    if (window.location.hostname === 'localhost') return;

    // Google Analytics page view
    if (window.gtag && GA_MEASUREMENT_ID && hasAnalyticsConsent()) {
      window.gtag('config', GA_MEASUREMENT_ID, {
        page_path: location.pathname + location.search,
      });
    }

    // Meta Pixel page view
    if (window.fbq && META_PIXEL_ID && hasMarketingConsent()) {
      window.fbq('track', 'PageView');
    }
  }, [location]);

  return null;
}

/**
 * Rastreia eventos customizados (respects consent)
 */
export function trackEvent(
  eventName: string,
  params?: Record<string, unknown>
) {
  if (window.location.hostname === 'localhost') {
    console.log('[Analytics] Event:', eventName, params);
    return;
  }

  // Google Analytics (only if consent)
  if (window.gtag && hasAnalyticsConsent()) {
    window.gtag('event', eventName, params);
  }

  // Meta Pixel (only if consent)
  if (window.fbq && hasMarketingConsent()) {
    window.fbq('trackCustom', eventName, params);
  }
}

/**
 * Rastreia conversões importantes
 */
export const analyticsEvents = {
  // Usuário iniciou trial
  startTrial: () => {
    trackEvent('start_trial');
    if (window.fbq && hasMarketingConsent()) window.fbq('track', 'StartTrial');
  },

  // Usuário clicou no CTA do WhatsApp
  clickWhatsApp: () => {
    trackEvent('click_whatsapp');
    if (window.fbq && hasMarketingConsent()) window.fbq('track', 'Contact');
  },

  // Usuário assinou
  subscribe: (plan: 'basic' | 'pro', value: number, currency: string) => {
    trackEvent('purchase', { plan, value, currency });
    if (window.fbq && hasMarketingConsent()) {
      window.fbq('track', 'Purchase', { value, currency });
    }
  },

  // Usuário trocou idioma
  changeLanguage: (locale: string) => {
    trackEvent('change_language', { locale });
  },
};

export default Analytics;
